import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Grid, Box, Tooltip, IconButton } from '@mui/material';
import { useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IJobBoard, usersAdvanceFilterQuery } from 'wisestep-api';
import { ControlledTextInput, WsAutoComplete, WsButton } from 'wisestep-base';
import { object, string } from 'yup';

import { BoldLabel } from './SharedUserList';

const validationSchema = object().shape({
  to: string().when('totalPostingsAllowed', {
    is: (totalPostingsAllowed) => totalPostingsAllowed === undefined,
    then: string().required('Email is required'),
    otherwise: string().nullable(),
  }),
  maxPostingsPerMonth: string().when('totalPostingsAllowed', {
    is: (totalPostingsAllowed) => totalPostingsAllowed === undefined,
    then: string().required('Credits are required'),
    otherwise: string().nullable(),
  }),
});

type PropTypes = {
  onSaveEmail(data: FieldValues): Promise<boolean>;
  jobBoardName?: string;
  selectedAccount?: IJobBoard;
};

const ShareQuota = ({ onSaveEmail, jobBoardName, selectedAccount }: PropTypes) => {
  const [suggestionsData, setSuggestionData] = useState<string[]>([]);
  const [sEmail, setSEmail] = useState<string>('');
  const workspaceRecruiterSearchAPI = async (query: string) => {
    if (query.length >= 2) {
      const tempArray: string[] = [];
      const workspaceRecruiterSuggestions = await usersAdvanceFilterQuery(query);
      workspaceRecruiterSuggestions.data.map((data) => tempArray.push(data?.userId));
      setSuggestionData(tempArray);
    }
  };
  const onEmailSelect = (selectedEmail: string[]) => {
    setValue('to', selectedEmail[0]);
    setSEmail(selectedEmail[0]);
  };

  const formOptions = {
    resolver: yupResolver(validationSchema),
  };

  const methods = useForm(formOptions);
  const navigate = useNavigate();
  const refresh = () => navigate(0);

  const { handleSubmit, reset, setValue } = methods;

  return (
    <Box pl={3} pr={3}>
      <FormProvider {...methods}>
        <Grid container component="form" display="flex" spacing={2}>
          {jobBoardName == 'Dice' && (
            <Grid container alignItems="space-between" pl={2}>
              <Grid item xs={7}>
                <BoldLabel sx={{ mb: 0.5 }}>
                  Total Job Posting Credits Available For The Account
                </BoldLabel>
                <ControlledTextInput
                  name="totalPostingsAllowed"
                  type="name"
                  defaultValue={selectedAccount?.totalPostingsAllowed}
                  fullWidth
                  placeholder="Total Job Posting Credits"
                  id="outlined-basic"
                  variant="outlined"
                  required={true}
                />
              </Grid>
              <Grid item xs={3}>
                <Box pt={4}>
                  <Tooltip title="Add the credits available in inventory to manage credits allocation to users">
                    <IconButton>
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={2} pt={4} justifyContent="flex-end" alignContent="flex-end">
                <WsButton
                  variant="contained"
                  size="large"
                  type="submit"
                  onClick={handleSubmit(onSaveEmail)}
                >
                  Save
                </WsButton>
              </Grid>
            </Grid>
          )}
          <Grid item xs={5}>
            <BoldLabel sx={{ mb: 0.5 }}>Share with *</BoldLabel>
            <WsAutoComplete
              onValueEnter={workspaceRecruiterSearchAPI}
              onTagsChange={onEmailSelect}
              placeholder="workspace user email"
              initialValue={[]}
              optionsInStringArray={suggestionsData}
              multiple={false}
              freeSolo={false}
              name="to"
              size="small"
              required={true}
              variant="outlined"
              value={sEmail}
            />
          </Grid>
          <Grid item xs={4.5}>
            <BoldLabel sx={{ mb: 0.5 }}>{`Max postings allowed ${
              jobBoardName == 'Dice' || jobBoardName == 'TechFetch' ? '' : '/month'
            }`}</BoldLabel>
            <ControlledTextInput
              name="maxPostingsPerMonth"
              type="text"
              fullWidth
              id="outlined-basic"
              placeholder="Enter max posting allowed"
              required
            />
          </Grid>
          <Grid item xs={2.5} alignSelf="end" pb={0.6}>
            <WsButton
              variant="contained"
              size="large"
              onClick={() => {
                handleSubmit(async (data) => {
                  const canRefresh = await onSaveEmail({ ...data });
                  if (canRefresh) {
                    refresh();
                  }
                })();
                reset();
                setSEmail('');
              }}
            >
              Share Quota
            </WsButton>
          </Grid>
        </Grid>
      </FormProvider>
    </Box>
  );
};

export default ShareQuota;
